import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Spinner, MessageBarType, MessageBar } from '@fluentui/react';

import { HealAPI } from '../../healApi';
import { InlineEditField } from '../../components/core/InlineEditField';
import { AgencyIdentity } from '../../redux/concierge/types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from '../../redux/message/actions';
import { RootState } from '../../redux/types';

interface ManagingAgencyProps {
  userAccountId: string;
  readonly?: boolean;
}

export const ManagingAgencies = ({ userAccountId, readonly }: ManagingAgencyProps): JSX.Element => {
  const dispatch = useDispatch();
  const partners = useSelector((state: RootState) => state.concierge.agencies);
  const [managingAgencies, setManagingAgencies] = useState<AgencyIdentity[]>();
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string>();

  const fetchManagingAgencies = useCallback(() => {
    setIsFetching(true);
    setError(undefined);
    HealAPI.fetchAccountManagingAgencies(userAccountId).then((response) => {
      if (response.ok && response.data) {
        setManagingAgencies(response.data.data);
      } else {
        setError("An error occurred while fetching this account's managing agencies, please try to refresh the page.");
      }
      setIsFetching(false);
    });
  }, [userAccountId]);

  useEffect(() => {
    fetchManagingAgencies();
  }, [fetchManagingAgencies]);

  const saveManagingAgencies = (newValue: any): any => {
    return HealAPI.setAccountManagingAgencies(userAccountId, newValue.partners)
      .then((response) => {
        if (response.ok && response.data) {
          dispatch(
            toast({
              type: 'success',
              title: 'Success',
              content: 'Account partner agencies have been successfully updated.',
            })
          );
          fetchManagingAgencies();
          return true;
        } else {
          dispatch(
            toast({
              type: 'error',
              title: 'Error',
              content: "Account partner agencies haven't been updated, please make sure you have the correct permission and try again.",
            })
          );
          return false;
        }
      })
      .catch(() => false);
  };

  if (isFetching) {
    return <Spinner style={{ alignItems: 'flex-start' }} />;
  }

  if (error) {
    return (
      <div style={{ marginTop: 10, maxWidth: 400 }}>
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      </div>
    );
  }

  if (readonly && (!managingAgencies || !managingAgencies.length)) {
    return <div>No partners</div>;
  }

  return (
    <InlineEditField
      initialValues={managingAgencies?.map((a) => a.id) || []}
      fieldNames={['partners']}
      width={300}
      readonly={readonly}
      options={partners?.map((p) => {
        return { key: p.id, text: p.name };
      })}
      onSubmit={saveManagingAgencies}
    />
  );
};
